import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { cartService } from '../services/api';

// État initial
const initialState = {
  items: [],
  subtotal: 0,
  totalItems: 0,
  isLoading: false,
  error: null,
};

// Types d'actions
const CART_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_CART: 'SET_CART',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  CLEAR_CART: 'CLEAR_CART',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case CART_ACTIONS.SET_CART:
      return {
        ...state,
        items: action.payload.items || [],
        subtotal: action.payload.subtotal || 0,
        totalItems: action.payload.total_items || 0,
        isLoading: false,
        error: null,
      };

    case CART_ACTIONS.ADD_ITEM:
      return {
        ...state,
        items: [...state.items, action.payload],
        isLoading: false,
        error: null,
      };

    case CART_ACTIONS.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? action.payload : item
        ),
        isLoading: false,
        error: null,
      };

    case CART_ACTIONS.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
        isLoading: false,
        error: null,
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: [],
        subtotal: 0,
        totalItems: 0,
        isLoading: false,
        error: null,
      };

    case CART_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case CART_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Créer le contexte
const CartContext = createContext();

// Provider
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Charger le panier au démarrage (seulement si authentifié)
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      loadCart();
    }
  }, []);

  // Charger le panier
  const loadCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

      const response = await cartService.getCart();

      if (response.success) {
        dispatch({
          type: CART_ACTIONS.SET_CART,
          payload: response.data,
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
      // Ne pas afficher d'erreur si l'utilisateur n'est pas connecté
      if (error.response?.status !== 401) {
        dispatch({
          type: CART_ACTIONS.SET_ERROR,
          payload: 'Erreur lors du chargement du panier',
        });
      } else {
        dispatch({ type: CART_ACTIONS.SET_LOADING, payload: false });
      }
    }
  };

  // Ajouter un produit au panier
  const addToCart = async (productId, quantity = 1) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

      const response = await cartService.addToCart(productId, quantity);

      if (response.success) {
        // Recharger le panier pour avoir les données à jour
        await loadCart();
        return { success: true };
      } else {
        dispatch({
          type: CART_ACTIONS.SET_ERROR,
          payload: response.message || 'Erreur lors de l\'ajout au panier',
        });
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'ajout au panier';
      dispatch({
        type: CART_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Mettre à jour la quantité d'un produit
  const updateQuantity = async (itemId, quantity) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

      const response = await cartService.updateCartItem(itemId, quantity);

      if (response.success) {
        // Recharger le panier pour avoir les données à jour (sous-total, etc.)
        await loadCart();
        return { success: true, message: 'Quantité mise à jour' };
      } else {
        const errorMsg = response.message || 'Erreur lors de la mise à jour';
        dispatch({
          type: CART_ACTIONS.SET_ERROR,
          payload: errorMsg,
        });
        dispatch({ type: CART_ACTIONS.SET_LOADING, payload: false });
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour';
      dispatch({
        type: CART_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: false });
      return { success: false, error: errorMessage };
    }
  };

  // Supprimer un produit du panier
  const removeFromCart = async (itemId) => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

      const response = await cartService.removeFromCart(itemId);

      if (response.success) {
        dispatch({
          type: CART_ACTIONS.REMOVE_ITEM,
          payload: itemId,
        });
        return { success: true };
      } else {
        dispatch({
          type: CART_ACTIONS.SET_ERROR,
          payload: response.message || 'Erreur lors de la suppression',
        });
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      dispatch({
        type: CART_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Vider le panier
  const clearCart = async () => {
    try {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: true });

      const response = await cartService.clearCart();

      if (response.success) {
        dispatch({ type: CART_ACTIONS.CLEAR_CART });
        return { success: true };
      } else {
        dispatch({
          type: CART_ACTIONS.SET_ERROR,
          payload: response.message || 'Erreur lors du vidage du panier',
        });
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors du vidage du panier';
      dispatch({
        type: CART_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Fusionner le panier (quand l'utilisateur se connecte)
  const mergeCart = async () => {
    try {
      const response = await cartService.mergeCart();

      if (response.success) {
        await loadCart(); // Recharger le panier fusionné
        return { success: true };
      } else {
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la fusion du panier';
      return { success: false, error: errorMessage };
    }
  };

  // Effacer l'erreur
  const clearError = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_ERROR });
  };

  // Vérifier si un produit est dans le panier
  const isInCart = (productId) => {
    return state.items.some(item => item.product_id === productId);
  };

  // Obtenir la quantité d'un produit dans le panier
  const getProductQuantity = (productId) => {
    const item = state.items.find(item => item.product_id === productId);
    return item ? item.quantity : 0;
  };

  const value = {
    ...state,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    mergeCart,
    loadCart,
    clearError,
    isInCart,
    getProductQuantity,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart doit être utilisé dans un CartProvider');
  }
  return context;
};






